import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut,
  Calendar,
  MessageSquare,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  RefreshCw,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", color: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30" },
  confirmed: { label: "Bestätigt", color: "text-green-400 bg-green-400/10 border-green-400/30" },
  cancelled: { label: "Storniert", color: "text-red-400 bg-red-400/10 border-red-400/30" },
  new: { label: "Neu", color: "text-blue-400 bg-blue-400/10 border-blue-400/30" },
  read: { label: "Gelesen", color: "text-[#9A9A9A] bg-[#9A9A9A]/10 border-[#9A9A9A]/30" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`text-xs border px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(isoStr) {
  try {
    return new Date(isoStr).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bRes, cRes, nRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletter`, { withCredentials: true }),
      ]);
      setBookings(bRes.data);
      setContacts(cRes.data);
      setNewsletter(nRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateBooking = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}`,
        { status },
        { withCredentials: true }
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { withCredentials: true });
      setBookings((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const updateContact = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}`,
        { status },
        { withCredentials: true }
      );
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Nachricht wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const stats = [
    {
      label: "Buchungen gesamt",
      value: bookings.length,
      sub: `${bookings.filter((b) => b.status === "pending").length} ausstehend`,
    },
    {
      label: "Bestätigt",
      value: bookings.filter((b) => b.status === "confirmed").length,
    },
    {
      label: "Nachrichten",
      value: contacts.length,
      sub: `${contacts.filter((c) => c.status === "new").length} neu`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#050510] text-[#F0EDE8]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0D0D1A]/90 backdrop-blur-md border-b border-[#1A1A2E] px-6 py-4 flex items-center justify-between">
        <h1
          className="text-xl text-[#C9A84C] tracking-[0.15em]"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
          data-testid="admin-header-title"
        >
          Auralia Admin
        </h1>
        <div className="flex items-center gap-4">
          <button
            onClick={fetchAll}
            className="text-[#9A9A9A] hover:text-[#F0EDE8] transition-colors p-1"
            title="Aktualisieren"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={16} />
          </button>
          <span className="text-[#9A9A9A] text-sm hidden sm:inline">{user?.email}</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#9A9A9A] hover:text-[#F0EDE8] transition-colors text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline">Abmelden</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`admin-stat-${i}`}
            >
              <p className="text-[0.65rem] uppercase tracking-[0.25em] text-[#9A9A9A] mb-2">
                {s.label}
              </p>
              <p
                className="text-4xl text-[#C9A84C]"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {s.value}
              </p>
              {s.sub && (
                <p className="text-xs text-[#9A9A9A] mt-1">{s.sub}</p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-[#1A1A2E] mb-8">
          {[
            { id: "bookings", label: "Buchungen", Icon: Calendar, count: bookings.length },
            { id: "contacts", label: "Nachrichten", Icon: MessageSquare, count: contacts.length },
            { id: "newsletter", label: "Newsletter", Icon: MessageSquare, count: newsletter.length },
          ].map(({ id, label, Icon, count }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 pb-4 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-all duration-300 ${
                tab === id
                  ? "text-[#C9A84C] border-[#C9A84C]"
                  : "text-[#9A9A9A] border-transparent hover:text-[#F0EDE8]"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon size={14} />
              {label}
              <span className="bg-[#1A1A2E] text-[#9A9A9A] px-1.5 py-0.5 rounded text-xs">
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20 text-[#9A9A9A]">Lädt ...</div>
        ) : tab === "bookings" ? (
          <BookingsList
            bookings={bookings}
            onStatus={updateBooking}
            onDelete={deleteBooking}
          />
        ) : tab === "newsletter" ? (
          <NewsletterList subscribers={newsletter} />
        ) : (
          <ContactsList
            contacts={contacts}
            onStatus={updateContact}
            onDelete={deleteContact}
          />
        )}
      </div>
    </div>
  );
}

function BookingsList({ bookings, onStatus, onDelete }) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-24 text-[#9A9A9A]">
        Noch keine Buchungen vorhanden.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {bookings.map((b) => (
        <motion.div
          key={b.id}
          className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-xl p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid={`booking-row-${b.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <h3 className="text-[#F0EDE8] font-light">{b.service_name}</h3>
                <StatusBadge status={b.status} />
              </div>
              <p className="text-[#9A9A9A] text-sm">
                {b.name} · {b.email} · {b.phone}
              </p>
              <p className="text-[#9A9A9A] text-sm mt-1">
                Wunschtermin:{" "}
                <span className="text-[#F0EDE8]/70">{b.date}</span>
                {" · "}Eingegangen:{" "}
                <span className="text-[#F0EDE8]/70">{formatDate(b.created_at)}</span>
              </p>
              {b.message && (
                <p className="text-[#9A9A9A] text-sm mt-2 italic">
                  "{b.message}"
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value={b.status}
                onChange={(e) => onStatus(b.id, e.target.value)}
                className="bg-[#050510] border border-[#1A1A2E] text-[#F0EDE8] text-xs px-3 py-2 rounded focus:border-[#C9A84C] focus:outline-none"
                data-testid={`booking-status-select-${b.id}`}
              >
                <option value="pending">Ausstehend</option>
                <option value="confirmed">Bestätigt</option>
                <option value="cancelled">Storniert</option>
              </select>
              <button
                onClick={() => onDelete(b.id)}
                className="text-[#9A9A9A] hover:text-red-400 transition-colors p-2"
                data-testid={`booking-delete-${b.id}`}
                title="Buchung löschen"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ContactsList({ contacts, onStatus, onDelete }) {
  if (contacts.length === 0) {
    return (
      <div className="text-center py-24 text-[#9A9A9A]">
        Noch keine Nachrichten vorhanden.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {contacts.map((c) => (
        <motion.div
          key={c.id}
          className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-xl p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid={`contact-row-${c.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <h3 className="text-[#F0EDE8] font-light">{c.name}</h3>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-[#9A9A9A] text-sm">
                {c.email} · {formatDate(c.created_at)}
              </p>
              <p className="text-[#F0EDE8]/70 text-sm mt-3 leading-relaxed">
                {c.message}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value={c.status}
                onChange={(e) => onStatus(c.id, e.target.value)}
                className="bg-[#050510] border border-[#1A1A2E] text-[#F0EDE8] text-xs px-3 py-2 rounded focus:border-[#C9A84C] focus:outline-none"
                data-testid={`contact-status-select-${c.id}`}
              >
                <option value="new">Neu</option>
                <option value="read">Gelesen</option>
              </select>
              <button
                onClick={() => onDelete(c.id)}
                className="text-[#9A9A9A] hover:text-red-400 transition-colors p-2"
                data-testid={`contact-delete-${c.id}`}
                title="Nachricht löschen"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}


function NewsletterList({ subscribers }) {
  if (subscribers.length === 0) {
    return (
      <div className="text-center py-24 text-[#9A9A9A]">
        Noch keine Newsletter-Abonnenten.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {subscribers.map((s) => (
        <motion.div
          key={s.id}
          className="bg-[#0D0D1A] border border-[#1A1A2E] rounded-xl px-6 py-4 flex items-center justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid={`newsletter-row-${s.id}`}
        >
          <span className="text-[#F0EDE8] text-sm">{s.email}</span>
          <span className="text-[#9A9A9A] text-xs">{formatDate(s.subscribed_at)}</span>
        </motion.div>
      ))}
    </div>
  );
}
